import { getPlatform } from './config';
import { toBlock, toComment, type Block, type BlockRow, type Comment, type CommentRow } from './types';

// --- Client-side TTL cache ---
const CACHE_TTL = 30_000;
const MAX_CACHE_ENTRIES = 50;

interface CacheEntry<T> {
  data: T;
  expiry: number;
}

function getCached<T>(cache: Map<string, CacheEntry<T>>, key: string): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiry) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

function setCache<T>(cache: Map<string, CacheEntry<T>>, key: string, data: T): void {
  if (cache.size >= MAX_CACHE_ENTRIES) {
    const firstKey = cache.keys().next().value;
    if (firstKey !== undefined) cache.delete(firstKey);
  }
  cache.set(key, { data, expiry: Date.now() + CACHE_TTL });
}

const profileViewCache = new Map<string, CacheEntry<any>>();
const channelViewCache = new Map<string, CacheEntry<any>>();
const channelBlocksCache = new Map<string, CacheEntry<any>>();
const exploreChannelsCache = new Map<string, CacheEntry<any>>();
const exploreUsersCache = new Map<string, CacheEntry<any>>();
const exploreFeedCache = new Map<string, CacheEntry<any>>();
const profileBlocksCache = new Map<string, CacheEntry<any>>();

export function invalidateAllCaches(): void {
  profileViewCache.clear();
  channelViewCache.clear();
  channelBlocksCache.clear();
  exploreChannelsCache.clear();
  exploreUsersCache.clear();
  exploreFeedCache.clear();
  profileBlocksCache.clear();
}

function normalizeBaseUrl(url: string): string {
  return url.replace(/\/$/, '');
}

function authHeaders(accessToken?: string): HeadersInit {
  if (!accessToken) return { 'Content-Type': 'application/json' };
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
}

function withOptionalAuth(headers: HeadersInit, accessToken?: string): HeadersInit {
  if (!accessToken) return headers;
  return { ...headers, Authorization: `Bearer ${accessToken}` };
}

const VIEWER_NONCE_KEY = 'ego-viewer-nonce';
let viewerNoncePromise: Promise<string> | null = null;

async function getViewerNonce(): Promise<string> {
  if (!viewerNoncePromise) {
    viewerNoncePromise = (async () => {
      const storage = getPlatform().storage;
      const existing = await storage.getItem(VIEWER_NONCE_KEY);
      if (existing) return existing;
      const nonce = crypto.randomUUID();
      await storage.setItem(VIEWER_NONCE_KEY, nonce);
      return nonce;
    })();
  }
  return viewerNoncePromise;
}

export async function checkHandleAvailability(handle: string): Promise<{ available: boolean; reason?: string }> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(`${baseUrl}/api/profiles/check-handle?handle=${encodeURIComponent(handle)}`);
  if (!res.ok) throw new Error('Failed to check handle');
  return res.json();
}

export async function fetchLinkPreview(url: string, accessToken?: string): Promise<{
  imagePath: string | null;
  title: string | null;
  description: string | null;
  domain: string | null;
}> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(`${baseUrl}/api/blocks/preview`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ url }),
  });

  if (!res.ok) throw new Error('Failed to fetch preview');
  return res.json();
}

export async function fetchProtectedChannelBlocks(slug: string, unlockToken?: string): Promise<Block[]> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const nonce = await getViewerNonce();
  const headers: HeadersInit = { 'X-Viewer-Nonce': nonce };
  if (unlockToken) {
    headers['X-Channel-Unlock'] = unlockToken;
  }

  const res = await fetch(`${baseUrl}/api/channels/${slug}/blocks`, { headers });
  if (!res.ok) throw new Error('Failed to fetch channel blocks');

  const rows = (await res.json()) as BlockRow[];
  return rows.map(toBlock);
}

export interface ExploreFeedItem {
  id: string;
  type: string;
  title: string;
  content: string | null;
  imagePath: string | null;
  sourceUrl: string | null;
  sourceAuthor: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  channel: {
    id: string;
    title: string;
    slug: string;
    visibility: string;
  };
  profile: {
    id: string;
    handle: string;
    name: string;
    avatarPath: string | null;
  } | null;
}

export interface ExploreChannel {
  id: string;
  title: string;
  slug: string;
  accentColor: string | null;
  borderColor: string | null;
  blocksCount: number;
  updatedAt: string | null;
  handle: string | null;
  author: string | null;
}

export interface ExploreUser {
  id: string;
  handle: string;
  name: string;
  bio: string | null;
  avatarPath: string | null;
  publicChannelsCount: number;
  publicBlocksCount: number;
  createdAt: string | null;
}

export async function fetchExploreUsers(): Promise<ExploreUser[]> {
  const cached = getCached(exploreUsersCache, '_');
  if (cached) return cached;

  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(`${baseUrl}/api/explore/users`);
  if (!res.ok) throw new Error('Failed to load explore users');
  const data = await res.json();
  setCache(exploreUsersCache, '_', data);
  return data;
}

export async function fetchExploreChannels(): Promise<ExploreChannel[]> {
  const cached = getCached(exploreChannelsCache, '_');
  if (cached) return cached;

  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(`${baseUrl}/api/explore/channels`);
  if (!res.ok) throw new Error('Failed to load explore channels');
  const data = await res.json();
  setCache(exploreChannelsCache, '_', data);
  return data;
}

export async function fetchExploreFeed(cursor?: string, limit = 30): Promise<{
  items: ExploreFeedItem[];
  nextCursor: string | null;
}> {
  const cacheKey = `cursor:${cursor || 'initial'}`;
  const cached = getCached(exploreFeedCache, cacheKey);
  if (cached) return cached;

  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const query = new URLSearchParams();
  if (cursor) query.set('cursor', cursor);
  query.set('limit', String(limit));

  const res = await fetch(`${baseUrl}/api/explore?${query.toString()}`);
  if (!res.ok) throw new Error('Failed to load explore feed');
  const data = await res.json();
  setCache(exploreFeedCache, cacheKey, data);
  return data;
}

export interface ProfileViewChannel {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  url: string | null;
  accentColor: string | null;
  borderColor: string | null;
  position: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  userId: string;
  visibility: 'public' | 'private' | 'password' | 'hidden';
  hasPassword: boolean;
  blocksCount: number;
  handle: string;
}

export interface ProfileViewResponse {
  profile: {
    id: string;
    handle: string;
    name: string;
    bio: string | null;
    website: string | null;
    websiteUrl?: string | null;
    avatarPath: string | null;
  };
  isOwner: boolean;
  channels: ProfileViewChannel[];
}

export async function fetchProfileView(handle: string, accessToken?: string): Promise<ProfileViewResponse> {
  const cacheKey = `${handle}:${accessToken ? 'auth' : 'anon'}`;
  const cached = getCached(profileViewCache, cacheKey);
  if (cached) return cached;

  // Clear stale anon cache when making an authenticated request
  if (accessToken) {
    profileViewCache.delete(`${handle}:anon`);
  }

  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(
    `${baseUrl}/api/profiles/${encodeURIComponent(handle)}`,
    { headers: withOptionalAuth({ 'Content-Type': 'application/json' }, accessToken) },
  );
  if (!res.ok) throw new Error('Failed to load profile');
  const data = await res.json();
  setCache(profileViewCache, cacheKey, data);
  return data;
}

export interface ChannelViewResponse {
  profile: {
    id: string;
    handle: string;
    name: string;
    avatarPath: string | null;
  };
  channel: {
    id: string;
    title: string;
    slug: string;
    description: string | null;
    url: string | null;
    accentColor: string | null;
    borderColor: string | null;
    position: number | null;
    createdAt: string | null;
    updatedAt: string | null;
    userId: string;
    visibility: 'public' | 'private' | 'password' | 'hidden';
    hasPassword: boolean;
    passwordVersion?: number;
    handle: string;
    hasCustomBlockOrder?: boolean;
  };
  isOwner: boolean;
}

export interface ProfileBlocksResponse {
  profile: {
    id: string;
    handle: string;
    name: string;
    avatarPath: string | null;
  };
  isOwner: boolean;
  blocks: Array<{
    id: string;
    channelId: string;
    type: string;
    title: string;
    content: string | null;
    imagePath: string | null;
    sourceUrl: string | null;
    sourceAuthor: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    channel: {
      id: string;
      title: string;
      slug: string;
      visibility: string;
      updatedAt: string | null;
    };
    profile: {
      handle: string;
      name: string;
    };
  }>;
}

export async function fetchProfileBlocks(handle: string, accessToken?: string): Promise<ProfileBlocksResponse> {
  const cacheKey = `${handle}:${accessToken ? 'auth' : 'anon'}`;
  const cached = getCached(profileBlocksCache, cacheKey);
  if (cached) return cached;

  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(
    `${baseUrl}/api/profiles/${encodeURIComponent(handle)}/blocks`,
    { headers: withOptionalAuth({ 'Content-Type': 'application/json' }, accessToken) },
  );
  if (!res.ok) throw new Error('Failed to load profile blocks');
  const data = await res.json();
  setCache(profileBlocksCache, cacheKey, data);
  return data;
}

export async function fetchChannelView(
  handle: string,
  slug: string,
  accessToken?: string,
  unlockToken?: string,
): Promise<ChannelViewResponse> {
  const cacheKey = `${handle}:${slug}:${accessToken ? 'auth' : 'anon'}`;
  const cached = getCached(channelViewCache, cacheKey);
  if (cached) return cached;

  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const headers: HeadersInit = withOptionalAuth({ 'Content-Type': 'application/json' }, accessToken);
  if (unlockToken) {
    const nonce = await getViewerNonce();
    (headers as Record<string, string>)['X-Channel-Unlock'] = unlockToken;
    (headers as Record<string, string>)['X-Viewer-Nonce'] = nonce;
  }
  const res = await fetch(
    `${baseUrl}/api/profiles/${encodeURIComponent(handle)}/channels/${encodeURIComponent(slug)}`,
    { headers },
  );
  if (!res.ok) throw new Error('Failed to load channel');
  const data = await res.json();
  setCache(channelViewCache, cacheKey, data);
  return data;
}

export async function fetchChannelBlocksByHandle(
  handle: string,
  slug: string,
  options?: { unlockToken?: string; accessToken?: string },
): Promise<Block[]> {
  const cacheKey = `${handle}:${slug}:${options?.accessToken ? 'auth' : 'anon'}`;
  const cached = getCached(channelBlocksCache, cacheKey);
  if (cached) return cached;

  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const nonce = await getViewerNonce();
  const headers: HeadersInit = { 'X-Viewer-Nonce': nonce };
  if (options?.unlockToken) headers['X-Channel-Unlock'] = options.unlockToken;
  if (options?.accessToken) headers['Authorization'] = `Bearer ${options.accessToken}`;

  const res = await fetch(
    `${baseUrl}/api/profiles/${encodeURIComponent(handle)}/channels/${encodeURIComponent(slug)}/blocks`,
    { headers },
  );
  if (!res.ok) throw new Error('Failed to fetch channel blocks');
  const data = (await res.json()) as Block[];
  setCache(channelBlocksCache, cacheKey, data);
  return data;
}

export async function verifyChannelPasswordByHandle(
  handle: string,
  slug: string,
  password: string,
  accessToken?: string,
): Promise<{ valid: boolean; unlockToken?: string; expiresInSeconds?: number }> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const nonce = await getViewerNonce();
  const res = await fetch(
    `${baseUrl}/api/profiles/${encodeURIComponent(handle)}/channels/${encodeURIComponent(slug)}/unlock`,
    {
      method: 'POST',
      headers: { ...withOptionalAuth({ 'Content-Type': 'application/json' }, accessToken), 'X-Viewer-Nonce': nonce },
      body: JSON.stringify({ password }),
    },
  );

  if (!res.ok) throw new Error('Password verification failed');
  return res.json();
}

export async function resolveLegacyChannelSlug(
  slug: string,
  accessToken?: string,
): Promise<{ handle: string; slug: string }> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(
    `${baseUrl}/api/channels/${encodeURIComponent(slug)}/resolve`,
    { headers: withOptionalAuth({ 'Content-Type': 'application/json' }, accessToken) },
  );
  if (!res.ok) throw new Error('Failed to resolve channel URL');
  return res.json();
}

export async function triggerEgoComment(blockId: string, accessToken: string): Promise<Comment> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(`${baseUrl}/api/ego/comment`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ blockId }),
  });

  if (!res.ok) throw new Error('EGO could not respond');
  const row = (await res.json()) as CommentRow;
  return toComment(row);
}

export async function verifyChannelPassword(slug: string, password: string): Promise<{
  valid: boolean;
  unlockToken?: string;
}> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const nonce = await getViewerNonce();
  const res = await fetch(`${baseUrl}/api/channels/${slug}/verify-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Viewer-Nonce': nonce },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    throw new Error('Password verification failed');
  }
  return res.json();
}

export async function postComment(input: {
  blockId: string;
  content: string;
  authorName?: string;
  accessToken?: string;
}): Promise<Comment> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const headers = authHeaders(input.accessToken);

  const res = await fetch(`${baseUrl}/api/comments`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      blockId: input.blockId,
      content: input.content,
      authorName: input.authorName,
    }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to post comment');
  }

  const row = (await res.json()) as CommentRow;
  return toComment(row);
}

export interface ChatSessionSummary {
  id: string;
  title: string | null;
  mode: string | null;
  created_at: string | null;
  updated_at: string | null;
  parent_session_id: string | null;
}

export interface ChatHistoryMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
  source?: 'text' | 'voice';
}

export async function fetchChatStarters(mode: string): Promise<string[]> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(`${baseUrl}/api/chat/starters/${mode}`);
  if (!res.ok) return [];
  return res.json();
}

export async function fetchChatSessions(accessToken: string): Promise<ChatSessionSummary[]> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(`${baseUrl}/api/chat/sessions`, {
    headers: authHeaders(accessToken),
  });
  if (!res.ok) throw new Error('Failed to load sessions');
  return res.json();
}

export async function fetchChatHistory(accessToken: string, sessionId: string): Promise<{
  mode: string;
  messages: ChatHistoryMessage[];
}> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(`${baseUrl}/api/chat/history/${sessionId}`, {
    headers: authHeaders(accessToken),
  });
  if (!res.ok) throw new Error('Failed to load history');
  return res.json();
}

export async function renameChatSession(accessToken: string, sessionId: string, title: string): Promise<void> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(`${baseUrl}/api/chat/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ title }),
  });
  if (!res.ok) {
    throw new Error('Failed to rename session');
  }
}

export async function deleteChatSession(accessToken: string, sessionId: string): Promise<string[]> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(`${baseUrl}/api/chat/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error('Failed to delete session');
  }
  const data = await res.json();
  return data.deleted || [sessionId];
}

export interface RevealedChannel {
  id: string;
  title: string;
  slug: string;
  accentColor: string | null;
  borderColor: string | null;
  position: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  userId: string;
  visibility: 'hidden';
  hasPassword: boolean;
  blocksCount: number;
  handle: string;
  author: string;
  unlockToken: string;
  previews: Array<{
    id: string;
    type: string;
    title: string;
    content: string | null;
    imagePath: string | null;
    createdAt: string | null;
    channelId: string;
  } | null>;
}

export async function reorderChannelBlocks(
  channelId: string,
  positions: Array<{ id: string; position: number }>,
  accessToken: string,
): Promise<{ ok: boolean }> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(`${baseUrl}/api/channels/${encodeURIComponent(channelId)}/blocks/reorder`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ positions }),
  });
  if (!res.ok) throw new Error('Failed to reorder blocks');
  return res.json();
}

export async function resetChannelBlockOrder(
  channelId: string,
  accessToken: string,
): Promise<{ ok: boolean }> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const res = await fetch(`${baseUrl}/api/channels/${encodeURIComponent(channelId)}/blocks/reset-order`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  if (!res.ok) throw new Error('Failed to reset block order');
  return res.json();
}

export async function revealHiddenChannels(
  handle: string,
  password: string,
  accessToken?: string,
): Promise<RevealedChannel[]> {
  const baseUrl = normalizeBaseUrl(getPlatform().serverBaseUrl);
  const nonce = await getViewerNonce();
  const res = await fetch(
    `${baseUrl}/api/profiles/${encodeURIComponent(handle)}/reveal-hidden`,
    {
      method: 'POST',
      headers: {
        ...withOptionalAuth({ 'Content-Type': 'application/json' }, accessToken),
        'X-Viewer-Nonce': nonce,
      },
      body: JSON.stringify({ password }),
    },
  );

  if (!res.ok) throw new Error('Failed to reveal hidden channels');
  const data = await res.json();
  return data.channels || [];
}
